import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

class VariantImageCarousel extends Component {
  #currentIndex = 0;
  #container;
  #slides;
  #isScrolling = false;
  #scrollTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#scrollTimeout) {
      clearTimeout(this.#scrollTimeout);
    }
  }

  #init() {
    this.#slides = Array.from(this.querySelectorAll('.variant-carousel__slide'));
    if (this.#slides.length === 0) return;

    this.#createContainer();
    this.#setupListeners();
    this.#preloadImages();
    
    // Initial sync with selected variant
    requestAnimationFrame(() => {
      this.#syncWithVariant();
    });
  }

  #createContainer() {
    this.#container = document.createElement('div');
    this.#container.className = 'variant-carousel__container';
    this.#slides.forEach(slide => {
      const clonedSlide = slide.cloneNode(true);
      this.#container.appendChild(clonedSlide);
    });
    // Replace original slides with container
    while (this.firstChild) {
      this.removeChild(this.firstChild);
    }
    this.appendChild(this.#container);
    
    // Update slides reference to the cloned ones
    this.#slides = Array.from(this.#container.querySelectorAll('.variant-carousel__slide'));
  }

  #setupListeners() {
    // Handle scroll to detect center slide
    this.#container.addEventListener('scroll', this.#handleScroll.bind(this), { passive: true });
    
    // Handle swipe/touch
    let startX = 0;
    let isDragging = false;
    let startScrollLeft = 0;
    
    this.#container.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startScrollLeft = this.#container.scrollLeft;
      isDragging = true;
    }, { passive: true });
    
    this.#container.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      // Prevent default only if we're actually dragging
      const diff = Math.abs(e.touches[0].clientX - startX);
      if (diff > 10) {
        e.preventDefault();
      }
    }, { passive: false });
    
    this.#container.addEventListener('touchend', (e) => {
      if (!isDragging) return;
      isDragging = false;
      const endX = e.changedTouches[0].clientX;
      const diff = startX - endX;
      
      if (Math.abs(diff) > 50) {
        if (diff > 0) this.#next();
        else this.#previous();
      }
    }, { passive: true });
    
    // Click on side slides to select
    this.#slides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        if (index !== this.#currentIndex) {
          this.#selectSlide(index);
        }
      });
    });
    
    // Listen for variant changes from size picker or variant picker
    // Listen on closest section like other components do
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate.bind(this));
    
    // Keyboard navigation
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.#previous();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.#next();
      }
    });
    
    // Make carousel focusable for keyboard
    this.setAttribute('tabindex', '0');
    this.setAttribute('role', 'region');
    this.setAttribute('aria-label', 'Product variant images');
  }

  #handleScroll() {
    if (this.#isScrolling) return;
    
    clearTimeout(this.#scrollTimeout);
    this.#scrollTimeout = setTimeout(() => {
      const containerWidth = this.#container.offsetWidth;
      const scrollLeft = this.#container.scrollLeft;
      
      // Find which slide is centered
      const centerPos = scrollLeft + (containerWidth / 2);
      let closestIndex = 0;
      let closestDistance = Infinity;
      
      this.#slides.forEach((slide, index) => {
        const slideLeft = slide.offsetLeft - this.#container.offsetLeft;
        const slideCenter = slideLeft + (slide.offsetWidth / 2);
        const distance = Math.abs(centerPos - slideCenter);
        
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      
      if (closestIndex !== this.#currentIndex) {
        this.#currentIndex = closestIndex;
        this.#updateSelectedVariant();
      }
    }, 100);
  }

  #updateSelectedVariant() {
    const selectedSlide = this.#slides[this.#currentIndex];
    if (!selectedSlide) return;
    
    // Update aria-current
    this.#slides.forEach(slide => slide.removeAttribute('aria-current'));
    selectedSlide.setAttribute('aria-current', 'true');
    
    // Select variant via variant picker
    const variantId = selectedSlide.dataset.variantId;
    if (!variantId) return;
    
    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!variantPicker) return;
    
    // Find input for this variant by variant ID
    let targetInput = variantPicker.querySelector(`input[data-variant-id="${variantId}"], select option[data-variant-id="${variantId}"]`);
    
    // If not found, search all inputs (for cases where variant picker structure is different)
    if (!targetInput) {
      const allInputs = variantPicker.querySelectorAll('input[data-variant-id], select option[data-variant-id]');
      for (const input of allInputs) {
        if (input.dataset.variantId === String(variantId)) {
          targetInput = input;
          break;
        }
      }
    }
    
    if (targetInput && variantPicker.updateSelectedOption) {
      // Set flag to prevent URL updates (we're using cached data, no need to update URL)
      variantPicker.dataset.skipUrlUpdate = 'true';
      
      variantPicker.updateSelectedOption(targetInput);
      const url = variantPicker.buildRequestUrl(targetInput);
      
      // Check if we have cached data - if so, don't fetch (just use cache)
      const variantId = targetInput.dataset.variantId;
      const productId = this.dataset.productId;
      const cacheKey = `${productId}-${variantId}`;
      const cached = window?.ThemeVariantCache?.get(cacheKey);
      
      if (cached?.html && cached?.variant) {
        // Use cached data - skip network request and URL update
        const html = new DOMParser().parseFromString(cached.html, 'text/html');
        const sourceId = targetInput.dataset.optionValueId || '';
        
        // Dispatch variant update directly without fetching
        const variantUpdateEvent = new VariantUpdateEvent(cached.variant, sourceId, {
          html,
          productId: productId,
          newProduct: undefined,
        });
        
        // Dispatch on the variant picker so it updates internal state
        variantPicker.dispatchEvent(variantUpdateEvent);
        
        // Clear flag
        delete variantPicker.dataset.skipUrlUpdate;
      } else {
        // No cache - do normal fetch (but skip URL update)
        variantPicker.fetchUpdatedSection(url, false);
        delete variantPicker.dataset.skipUrlUpdate;
      }
    }
  }

  #next() {
    if (this.#currentIndex < this.#slides.length - 1) {
      this.#selectSlide(this.#currentIndex + 1);
    }
  }

  #previous() {
    if (this.#currentIndex > 0) {
      this.#selectSlide(this.#currentIndex - 1);
    }
  }

  #selectSlide(index) {
    if (index < 0 || index >= this.#slides.length) return;
    
    this.#isScrolling = true;
    const slide = this.#slides[index];
    const containerWidth = this.#container.offsetWidth;
    const slideLeft = slide.offsetLeft - this.#container.offsetLeft;
    const scrollTarget = slideLeft - (containerWidth / 2) + (slide.offsetWidth / 2);
    
    this.#container.scrollTo({
      left: Math.max(0, scrollTarget),
      behavior: 'smooth'
    });
    
    this.#currentIndex = index;
    
    // Reset scrolling flag after animation
    setTimeout(() => {
      this.#isScrolling = false;
      this.#updateSelectedVariant();
    }, 400);
  }

  #onVariantUpdate(event) {
    // Sync carousel when variant changes from size picker
    const variantId = event.detail?.resource?.id;
    if (!variantId) return;
    
    const matchingSlide = this.#slides.find(slide => slide.dataset.variantId === String(variantId));
    if (matchingSlide) {
      const index = this.#slides.indexOf(matchingSlide);
      if (index !== this.#currentIndex) {
        this.#selectSlide(index);
      }
    }
  }

  #syncWithVariant() {
    // Check if there's a currently selected variant in the variant picker
    const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
    if (!variantPicker) return;
    
    const selectedInput = variantPicker.querySelector('input:checked, select option:checked, select option[selected]');
    if (!selectedInput) return;
    
    const variantId = selectedInput.dataset.variantId || (selectedInput instanceof HTMLOptionElement ? selectedInput.value : null);
    if (!variantId) return;
    
    const matchingSlide = this.#slides.find(slide => slide.dataset.variantId === String(variantId));
    if (matchingSlide) {
      const index = this.#slides.indexOf(matchingSlide);
      if (index !== this.#currentIndex) {
        this.#selectSlide(index);
      }
    }
  }

  #preloadImages() {
    // Pre-load all images for smooth carousel
    this.#slides.forEach((slide, index) => {
      const img = slide.querySelector('img');
      if (!img) return;
      
      // Pre-load high-res version
      const highResUrl = img.dataset.highRes;
      if (highResUrl && !img.complete) {
        const highResImg = new Image();
        highResImg.src = highResUrl;
      }
      
      // Also check cache for variant images
      const variantId = slide.dataset.variantId;
      const productId = this.dataset.productId;
      if (variantId && productId) {
        const cacheKey = `${productId}-${variantId}`;
        const cached = window?.ThemeVariantCache?.get(cacheKey);
        
        if (cached?.imageUrl && cached.imageUrl !== img.src) {
          // Pre-load cached image too
          const cachedImg = new Image();
          cachedImg.src = cached.imageUrl + '&width=1200';
        }
      }
    });
  }
}

if (!customElements.get('variant-image-carousel')) {
  customElements.define('variant-image-carousel', VariantImageCarousel);
}

